import { useState, useRef, useCallback } from 'react';

/**
 * Custom hook for managing all chat state
 * Centralizes state management for ChatPage
 */
export const useChatState = (user) => {
  // Room states
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [pendingJoinRoom, setPendingJoinRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [members, setMembers] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);
  const [showPinned, setShowPinned] = useState(false);
  
  // Room creation states
  const [createRoomOpen, setCreateRoomOpen] = useState(false);
  const [newRoomData, setNewRoomData] = useState({ name: '', type: 'public', description: '' });
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomDescription, setNewRoomDescription] = useState('');
  const [isPrivateRoom, setIsPrivateRoom] = useState(false);
  
  // Message input states
  const [messageInput, setMessageInput] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [mentionSearch, setMentionSearch] = useState('');
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionResults, setMentionResults] = useState([]);
  
  // Friends states
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [pmRequests, setPmRequests] = useState([]);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [pmMessages, setPmMessages] = useState([]);
  const [pmInput, setPmInput] = useState('');
  
  // PM Windows states
  const [openPMWindows, setOpenPMWindows] = useState([]);
  const [pmQueue, setPmQueue] = useState([]);
  const [unreadPmCount, setUnreadPmCount] = useState(0);
  const [unreadBySender, setUnreadBySender] = useState([]);
  
  // UI states
  const [activeTab, setActiveTab] = useState('rooms');
  const [mobileNavTab, setMobileNavTab] = useState('chats');
  const [friendsTabSection, setFriendsTabSection] = useState('friends');
  const [searchQuery, setSearchQuery] = useState('');
  const [roomSearchQuery, setRoomSearchQuery] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showMobileUserMenu, setShowMobileUserMenu] = useState(false);
  
  // Modal states
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [roomSettingsOpen, setRoomSettingsOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState({ reported_user_id: '', reason: '' });
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedMemberProfile, setSelectedMemberProfile] = useState(null);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [showGamesModal, setShowGamesModal] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [leaveRoomDialogOpen, setLeaveRoomDialogOpen] = useState(false);
  const [leaveRoomLoading, setLeaveRoomLoading] = useState(false);
  const [roomToLeave, setRoomToLeave] = useState(null);
  
  // Media states
  const [lightboxMedia, setLightboxMedia] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showMediaMenu, setShowMediaMenu] = useState(false);
  const [showInputOptions, setShowInputOptions] = useState(false);
  
  // Emoji/GIF states
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGiphyPicker, setShowGiphyPicker] = useState(false);
  const [giphySearch, setGiphySearch] = useState('');
  const [giphyResults, setGiphyResults] = useState([]);
  const [showPmEmojiPicker, setShowPmEmojiPicker] = useState(false);
  const [showPmGiphyPicker, setShowPmGiphyPicker] = useState(false);
  const [pmGiphySearch, setPmGiphySearch] = useState('');
  const [pmGiphyResults, setPmGiphyResults] = useState([]);
  
  // Games states
  const [games, setGames] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  
  // Settings states
  const [pmSettings, setPmSettings] = useState({ accept_pms: true });
  
  // Theme states
  const [globalTheme, setGlobalTheme] = useState({
    font_size: 14,
    font_color: '#000000',
    notification_color: '#3B82F6',
    chat_bubble_own: '#2563EB',
    chat_bubble_other: '#FFFFFF'
  });
  const [roomTheme, setRoomTheme] = useState({ font_family: '', font_color: '#000000' });
  
  // Notifications states
  const [adminNotifications, setAdminNotifications] = useState([]);
  const [dismissedNotifications, setDismissedNotifications] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('dismissed_notifications') || '[]');
    } catch {
      return [];
    }
  });
  
  // WebSocket states
  const [ws, setWs] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  
  // Refs
  const messagesEndRef = useRef(null);
  const pmMessagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  
  // Computed values
  const filteredRooms = rooms.filter((room) =>
    room.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const visibleNotifications = adminNotifications.filter(
    n => !dismissedNotifications.includes(n.id)
  );
  
  const needsVerification = user && user.needs_verification === true;
  
  // Utility functions
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  
  const scrollPmToBottom = useCallback(() => {
    pmMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  
  const dismissNotification = useCallback((notificationId) => {
    const newDismissed = [...dismissedNotifications, notificationId];
    setDismissedNotifications(newDismissed);
    localStorage.setItem('dismissed_notifications', JSON.stringify(newDismissed));
  }, [dismissedNotifications]);
  
  return {
    // Room states
    rooms, setRooms,
    selectedRoom, setSelectedRoom,
    pendingJoinRoom, setPendingJoinRoom,
    messages, setMessages,
    isLoadingMore, setIsLoadingMore,
    hasMoreMessages, setHasMoreMessages,
    members, setMembers,
    pinnedMessages, setPinnedMessages,
    showPinned, setShowPinned,
    
    // Room creation states
    createRoomOpen, setCreateRoomOpen,
    newRoomData, setNewRoomData,
    newRoomName, setNewRoomName,
    newRoomDescription, setNewRoomDescription,
    isPrivateRoom, setIsPrivateRoom,
    
    // Message input states
    messageInput, setMessageInput,
    replyingTo, setReplyingTo,
    mentionSearch, setMentionSearch,
    showMentionDropdown, setShowMentionDropdown,
    mentionResults, setMentionResults,
    
    // Friends states
    friends, setFriends,
    friendRequests, setFriendRequests,
    pmRequests, setPmRequests,
    blockedUsers, setBlockedUsers,
    staffList, setStaffList,
    selectedFriend, setSelectedFriend,
    pmMessages, setPmMessages,
    pmInput, setPmInput,
    
    // PM Windows states
    openPMWindows, setOpenPMWindows,
    pmQueue, setPmQueue,
    unreadPmCount, setUnreadPmCount,
    unreadBySender, setUnreadBySender,
    
    // UI states
    activeTab, setActiveTab,
    mobileNavTab, setMobileNavTab,
    friendsTabSection, setFriendsTabSection,
    searchQuery, setSearchQuery,
    roomSearchQuery, setRoomSearchQuery,
    userSearchQuery, setUserSearchQuery,
    userSearchResults, setUserSearchResults,
    mobileMenuOpen, setMobileMenuOpen,
    showMobileUserMenu, setShowMobileUserMenu,
    
    // Modal states
    settingsOpen, setSettingsOpen,
    roomSettingsOpen, setRoomSettingsOpen,
    reportOpen, setReportOpen,
    reportData, setReportData,
    showMembersModal, setShowMembersModal,
    selectedMemberProfile, setSelectedMemberProfile,
    showUserProfileModal, setShowUserProfileModal,
    showGamesModal, setShowGamesModal,
    showProfileSettings, setShowProfileSettings,
    leaveRoomDialogOpen, setLeaveRoomDialogOpen,
    leaveRoomLoading, setLeaveRoomLoading,
    roomToLeave, setRoomToLeave,
    
    // Media states
    lightboxMedia, setLightboxMedia,
    uploadingFile, setUploadingFile,
    showMediaMenu, setShowMediaMenu,
    showInputOptions, setShowInputOptions,
    
    // Emoji/GIF states
    showEmojiPicker, setShowEmojiPicker,
    showGiphyPicker, setShowGiphyPicker,
    giphySearch, setGiphySearch,
    giphyResults, setGiphyResults,
    showPmEmojiPicker, setShowPmEmojiPicker,
    showPmGiphyPicker, setShowPmGiphyPicker,
    pmGiphySearch, setPmGiphySearch,
    pmGiphyResults, setPmGiphyResults,
    
    // Games states
    games, setGames,
    selectedGame, setSelectedGame,
    
    // Settings states
    pmSettings, setPmSettings,
    
    // Theme states
    globalTheme, setGlobalTheme,
    roomTheme, setRoomTheme,
    
    // Notifications states
    adminNotifications, setAdminNotifications,
    dismissedNotifications, setDismissedNotifications,
    visibleNotifications,
    
    // WebSocket states
    ws, setWs,
    wsConnected, setWsConnected,
    reconnecting, setReconnecting,
    
    // Refs
    messagesEndRef,
    pmMessagesEndRef,
    wsRef,
    fileInputRef,
    messageInputRef,
    reconnectAttemptsRef,
    
    // Computed values
    filteredRooms,
    needsVerification,
    
    // Utility functions
    scrollToBottom,
    scrollPmToBottom,
    dismissNotification
  };
};

export default useChatState;
